import { Id } from "@teachloop/backend/_generated/dataModel";
import { useMemo } from "react";

type Student = {
  _id: Id<"students">;
  name: string;
  keyStageId: Id<"keyStages">;
  keyStage: {
    label?: string;
    value?: number;
  };
};

type PriceInfo = {
  tutorHourlyCredits: number;
  platformHourlyCredits: number;
  inPersonLessonCreditsFee: number;
  totalCredits: number;
  cents: number;
};

type LessonPrices = Record<
  Id<"students">,
  {
    remote?: PriceInfo;
    inPerson?: PriceInfo;
  }
>;

type LessonQuantities = Record<
  Id<"students">,
  {
    remote?: number;
    inPerson?: number;
  }
>;

export default function useLessonCreditsBuilder(
  students: Student[],
  lessonPrices: LessonPrices,
  lessonQuantities: LessonQuantities
) {
  return useMemo(() => {
    const items = students.flatMap((student) => {
      const modes: ("remote" | "inPerson")[] = ["remote", "inPerson"];

      return modes
        .map((mode) => {
          const priceData = lessonPrices[student._id]?.[mode];
          const quantity = lessonQuantities[student._id]?.[mode] ?? 0;

          if (!priceData || quantity <= 0) return null;

          return {
            studentId: student._id,
            keyStageId: student.keyStageId,
            quantity,
            studentSnapshot: { firstName: student.name },
            keyStageSnapshot: {
              label: student.keyStage.label ?? "",
              value: student.keyStage.value ?? 0,
            },
            priceSnapshot: {
              tutorHourlyCredits: priceData.tutorHourlyCredits,
              platformHourlyCredits: priceData.platformHourlyCredits,
              inPersonLessonCreditsFee: priceData.inPersonLessonCreditsFee,
              totalCredits: priceData.totalCredits,
              cents: priceData.cents,
            },
          };
        })
        .filter(Boolean);
    }) as {
      studentId: Id<"students">;
      keyStageId: Id<"keyStages">;
      quantity: number;
      studentSnapshot: { firstName: string };
      keyStageSnapshot: { label: string; value: number };
      priceSnapshot: PriceInfo;
    }[];

    const totalCredits = items.reduce(
      (acc, i) => acc + i.priceSnapshot.totalCredits * i.quantity,
      0
    );

    const totalCents = items.reduce(
      (acc, i) => acc + i.priceSnapshot.cents * i.quantity,
      0
    );

    return { totalCredits, totalCents, items };
  }, [students, lessonPrices, lessonQuantities]);
}
